import React, { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Image, Calendar, RefreshCw, ChevronDown, ChevronUp, Trash2, Zap } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface StrategySignal {
  id: string;
  ticker_symbol: string;
  signal_date: string;
  file_path: string;
  file_name: string;
  signal_type: string | null;
  notes: string | null;
  created_at: string;
}

const StrategySignalsTab = () => {
  const { toast } = useToast();
  const [signals, setSignals] = useState<StrategySignal[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedTicker, setSelectedTicker] = useState<string>('all');
  const [expandedSignal, setExpandedSignal] = useState<string | null>(null);
  const [signalToDelete, setSignalToDelete] = useState<StrategySignal | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Helper to parse date string without timezone shift
  const parseLocalDate = (dateStr: string): Date => {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  // Get today's date in YYYY-MM-DD format (Eastern Time)
  const getTodayET = (): string => {
    const now = new Date();
    const etOptions: Intl.DateTimeFormatOptions = { 
      timeZone: 'America/New_York', 
      year: 'numeric', 
      month: '2-digit', 
      day: '2-digit' 
    };
    const parts = new Intl.DateTimeFormat('en-CA', etOptions).formatToParts(now);
    const year = parts.find(p => p.type === 'year')?.value;
    const month = parts.find(p => p.type === 'month')?.value;
    const day = parts.find(p => p.type === 'day')?.value;
    return `${year}-${month}-${day}`;
  };

  // Check if current time is before 3:50 PM ET
  const isBeforeSignalUpload = (): boolean => {
    const now = new Date();
    const etTime = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));
    const hours = etTime.getHours();
    const minutes = etTime.getMinutes();
    return hours < 15 || (hours === 15 && minutes < 50);
  };

  const todayET = getTodayET();
  const showTodayPlaceholder = isBeforeSignalUpload() && !signals.some(s => s.signal_date === todayET);
  const uniqueTickers = [...new Set(signals.map(s => s.ticker_symbol))].sort();

  const fetchSignals = async () => {
    setIsLoading(true);
    try {
      let query = supabase
        .from('strategy_signals')
        .select('*')
        .order('signal_date', { ascending: false })
        .order('created_at', { ascending: false });

      if (selectedDate) {
        query = query.eq('signal_date', selectedDate);
      }

      if (selectedTicker && selectedTicker !== 'all') {
        query = query.eq('ticker_symbol', selectedTicker);
      }

      const { data, error } = await query.limit(100);

      if (error) throw error;
      setSignals(data || []);
    } catch (error: any) {
      console.error('Error fetching strategy signals:', error);
      toast({
        title: "Error",
        description: "Failed to load strategy signals.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSignals();
  }, [selectedDate, selectedTicker]);

  const getSignedUrl = async (filePath: string): Promise<string | null> => {
    try {
      const { data, error } = await supabase.storage
        .from('strategy-signals')
        .createSignedUrl(filePath, 3600); // 1 hour expiry

      if (error) throw error;
      return data.signedUrl;
    } catch (error) {
      console.error('Error getting signed URL:', error);
      return null;
    }
  };

  const handleDeleteSignal = async () => {
    if (!signalToDelete) return;
    
    setIsDeleting(true);
    try {
      // Delete from storage first
      const { error: storageError } = await supabase.storage
        .from('strategy-signals')
        .remove([signalToDelete.file_path]);

      if (storageError) {
        console.warn('Storage delete error (may not exist):', storageError);
      }

      // Delete metadata record
      const { error: dbError } = await supabase
        .from('strategy_signals')
        .delete()
        .eq('id', signalToDelete.id);

      if (dbError) throw dbError;

      toast({
        title: "Signal Deleted",
        description: `Removed ${signalToDelete.ticker_symbol} signal from ${signalToDelete.signal_date}.`,
      });

      // Refresh list
      fetchSignals();
    } catch (error: any) {
      console.error('Error deleting signal:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to delete signal.",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
      setSignalToDelete(null);
    }
  };

  const SignalCard = ({ signal }: { signal: StrategySignal }) => {
    const [imageUrl, setImageUrl] = useState<string | null>(null);
    const [loadingImage, setLoadingImage] = useState(false);
    const isExpanded = expandedSignal === signal.id;

    const loadImage = async () => {
      if (imageUrl) return;
      setLoadingImage(true);
      const url = await getSignedUrl(signal.file_path);
      setImageUrl(url);
      setLoadingImage(false);
    };

    useEffect(() => {
      if (isExpanded) {
        loadImage();
      }
    }, [isExpanded]);

    return (
      <Card className="bg-gradient-to-br from-delphi-dark to-delphi-dark/80 border border-delphi-gray/30 shadow-lg overflow-hidden">
        <div 
          className="p-4 cursor-pointer hover:bg-delphi-gray/10 transition-colors"
          onClick={() => setExpandedSignal(isExpanded ? null : signal.id)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-2 rounded-lg bg-delphi-red/20">
                <Image size={20} className="text-delphi-red" />
              </div>
              <div>
                <p className="font-bold text-delphi-light text-lg">{signal.ticker_symbol}</p>
                <div className="flex items-center gap-2 text-sm text-delphi-light/60">
                  <Calendar size={14} />
                  <span>{parseLocalDate(signal.signal_date).toLocaleDateString()}</span>
                  {signal.signal_type && (
                    <span className="px-2 py-0.5 rounded-full bg-delphi-red/20 text-delphi-red text-xs">
                      {signal.signal_type}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                onClick={(e) => {
                  e.stopPropagation();
                  setSignalToDelete(signal);
                }}
              >
                <Trash2 size={16} />
              </Button>
              {isExpanded ? (
                <ChevronUp size={20} className="text-delphi-light/60" />
              ) : (
                <ChevronDown size={20} className="text-delphi-light/60" />
              )}
            </div>
          </div>
        </div>

        {isExpanded && (
          <div className="border-t border-delphi-gray/20 p-4">
            {loadingImage ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-delphi-red" />
              </div>
            ) : imageUrl ? (
              <div className="space-y-4">
                <img 
                  src={imageUrl} 
                  alt={`${signal.ticker_symbol} signal`}
                  className="w-full rounded-lg border border-delphi-gray/30"
                />
                {signal.notes && (
                  <p className="text-delphi-light/70 text-sm">{signal.notes}</p>
                )}
                <p className="text-delphi-light/50 text-xs">
                  File: {signal.file_name} • Uploaded: {new Date(signal.created_at).toLocaleString()}
                </p>
              </div>
            ) : (
              <p className="text-delphi-light/50 text-center py-4">Failed to load image</p>
            )}
          </div>
        )}
      </Card>
    );
  };

  // Group signals by date for display
  const signalsByDate = signals.reduce((acc, signal) => {
    const date = signal.signal_date;
    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push(signal);
    return acc;
  }, {} as Record<string, StrategySignal[]>);

  return (
    <div className="space-y-6">
      {/* Filters */}
      <Card className="bg-gradient-to-br from-delphi-dark to-delphi-dark/80 border border-delphi-red/20 shadow-lg shadow-delphi-red/5">
        <CardHeader>
          <CardTitle className="text-xl text-delphi-light">Strategy Signals</CardTitle>
          <CardDescription>View uploaded model predictions and signal charts</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[150px]">
              <Label className="text-delphi-light/70 text-sm mb-2 block">Filter by Date</Label>
              <Input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="bg-delphi-dark text-delphi-light border-delphi-gray/40"
              />
            </div>
            <div className="flex-1 min-w-[150px]">
              <Label className="text-delphi-light/70 text-sm mb-2 block">Filter by Ticker</Label>
              <Select value={selectedTicker} onValueChange={setSelectedTicker}>
                <SelectTrigger className="bg-delphi-dark text-delphi-light border-delphi-gray/40">
                  <SelectValue placeholder="All tickers" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All tickers</SelectItem>
                  {uniqueTickers.map(ticker => (
                    <SelectItem key={ticker} value={ticker}>{ticker}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button 
              variant="outline" 
              onClick={() => {
                setSelectedDate('');
                setSelectedTicker('all');
              }}
              className="border-delphi-gray/40 text-delphi-light/70 hover:bg-delphi-gray/20"
            >
              Clear Filters
            </Button>
            <Button 
              variant="outline" 
              onClick={fetchSignals}
              className="border-delphi-gray/40 text-delphi-light/70 hover:bg-delphi-gray/20"
            >
              <RefreshCw size={16} className="mr-2" />
              Refresh
            </Button>
            <Button 
              asChild
              className="bg-gradient-to-r from-delphi-red to-red-600 hover:from-red-600 hover:to-red-700 text-white font-semibold shadow-lg shadow-delphi-red/25"
            >
              <a
                href="https://github.com/ckingdelphi1/daily_strat_dashboard_v1/actions/workflows/run_weekdays_350pm_et.yml"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Zap size={16} className="mr-2" />
                Trigger Python Signals
              </a>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Signal List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-delphi-red" />
        </div>
      ) : signals.length === 0 ? (
        <Card className="bg-gradient-to-br from-delphi-dark to-delphi-dark/80 border border-delphi-gray/30">
          <CardContent className="py-12 text-center">
            <Image size={48} className="mx-auto text-delphi-light/30 mb-4" />
            <p className="text-delphi-light/50">No strategy signals found.</p>
            <p className="text-delphi-light/30 text-sm mt-2">
              Signals will appear here once uploaded via the Python script.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {/* Today's Placeholder - shows before 3:50 PM ET if no signals uploaded yet */}
          {showTodayPlaceholder && !selectedDate && (
            <div>
              <h3 className="text-lg font-semibold text-delphi-light mb-3 flex items-center gap-2">
                <Calendar size={18} className="text-delphi-red" />
                {parseLocalDate(todayET).toLocaleDateString('en-US', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
                <span className="text-sm font-normal text-yellow-500">
                  (pending)
                </span>
              </h3>
              <Card className="bg-gradient-to-br from-delphi-dark to-delphi-dark/80 border border-yellow-500/30 shadow-lg">
                <CardContent className="py-8 text-center">
                  <div className="flex items-center justify-center gap-3 text-yellow-500 mb-2">
                    <Loader2 size={20} className="animate-spin" />
                    <p className="font-medium">Will populate 10 minutes prior to market close</p>
                  </div>
                  <p className="text-delphi-light/50 text-sm">
                    Strategy signals for today will be uploaded automatically at 3:50 PM ET.
                  </p>
                </CardContent>
              </Card>
            </div>
          )}

          {Object.entries(signalsByDate).map(([date, dateSignals]) => (
            <div key={date}>
              <h3 className="text-lg font-semibold text-delphi-light mb-3 flex items-center gap-2">
                <Calendar size={18} className="text-delphi-red" />
                {parseLocalDate(date).toLocaleDateString('en-US', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
                <span className="text-sm font-normal text-delphi-light/50">
                  ({dateSignals.length} signal{dateSignals.length !== 1 ? 's' : ''})
                </span>
              </h3>
              <div className="grid gap-4">
                {dateSignals.map(signal => (
                  <SignalCard key={signal.id} signal={signal} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!signalToDelete} onOpenChange={() => setSignalToDelete(null)}>
        <AlertDialogContent className="bg-delphi-dark border-delphi-gray/40">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-delphi-light">Delete Strategy Signal</AlertDialogTitle>
            <AlertDialogDescription className="text-delphi-light/70">
              Are you sure you want to delete the {signalToDelete?.ticker_symbol} signal from {signalToDelete?.signal_date}? 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-delphi-gray/40 text-delphi-light hover:bg-delphi-gray/20">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteSignal}
              className="bg-red-600 hover:bg-red-700"
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default StrategySignalsTab;
